/* global bootstrap */

import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import Footer from "../Footer";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import _ from "lodash";
import UserContext from "../../utils/Contexts/UserContext";
import axiosInstance from "../../utils/Requests/axios";
import toast from "react-hot-toast";
import FilterIcon from "../svg/FilterIcon";
import MonitorTable from "./MonitorTable";
import MonitorFilterModal from "../../modals/MonitorFilterModal";
import TablePagination from "../Common/Pagination";

import "./style.css";

const Monitor = () => {
  const { t } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const [pageCount, setPageCount] = useState(15);
  const [activePage, setActivePage] = useState(1);
  const isClear = useRef(false);
  const [columns, setColumns] = useState({
    backlink: true,
    indexed: true,
    price: true,
    dr: true,
    ur: true,
    da: true,
    pa: true,
    traffic: true,
    ext: true,
    int: true,
    target: true,
    t_indexed: true,
    status: true,
    anchor: true,
    rel: true,
    type: true,
    contract: true,
    created: true,
    first_seen: true,
    last_seen: true,
  });

  const handleToggle = (column) => {
    setColumns((prevColumns) => ({
      ...prevColumns,
      [column]: !prevColumns[column],
    }));
  };

  const [columnSorts, setColumnSorts] = useState({
    0: null,
    1: null,
    2: null,
    3: null,
    4: null,
  });
  const [tableData, setTableData] = useState(null);
  const [search, setSearch] = useState("");
  const [activeFiltersLength, setActiveFiltersLength] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const isSubmit = useRef(false);
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [tempFilters, setTempFilters] = useState([]);
  const [formData, setFormData] = useState({
    draw: 1,
    columns: [
      {
        data: "anchor",
      },
      {
        data: "rel",
      },
      {
        data: "type",
      },
      {
        data: "status",
      },
      {
        data: "created",
      },
    ],
    order: [{ column: 4, dir: "asc" }],
    start: 0,
    length: 15,
    search: {
      value: "",
      filters: [
        {
          key: "STATUS",
          value: "",
        },
        {
          key: "DONOR_UUID",
          value: "",
        },
        {
          key: "TARGET_UUID",
          value: "",
        },
        {
          key: "REL",
          value: "",
        },
        {
          key: "TYPE",
          value: "",
        },
        {
          key: "DONOR_INDEXED",
          value: "",
        },
        {
          key: "TARGET_INDEXED",
          value: "",
        },
      ],
    },
  });

  const fetchMonitors = () => {
    const payload = _.cloneDeep(formData); // use base formData

    // 👇 Inject tempFilters into the payload before sending
    if (payload.search) {
      payload.search.filters = _.cloneDeep(tempFilters);
    }

    // 🔍 Clean the filters
    if (payload.search && payload.search.filters) {
      payload.search.filters = payload.search.filters.filter(
        (filter) =>
          filter.value !== "" &&
          filter.value !== null &&
          filter.value !== undefined &&
          !Number.isNaN(filter.value)
      );
      setActiveFiltersLength(payload.search.filters.length);
    }

    const url = `users/${userData?.uuid}/links/search`;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        const data = response?.data?.data || [];
        const filtered = response?.data?.recordsFiltered || 0;
        setTableData(data);
        setRecordsFiltered(filtered);
      })
      .catch(() => {
        toast.error(t("There was an error fetching backlinks monitors"));
      });
  };

  useEffect(() => {
    const order = Object.keys(columnSorts)
      .filter((columnIndex) => columnSorts[columnIndex])
      .map((columnIndex) => ({
        column: Number(columnIndex),
        dir: columnSorts[columnIndex],
      }));

    if (!_.isEqual(formData.order, order)) {
      setFormData((prev) => ({
        ...prev,
        order,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSorts]);

  useEffect(() => {
    const payload = { ...formData };
    const start = activePage === 1 ? 0 : (activePage - 1) * pageCount;
    if (payload.start !== start || payload.length !== pageCount) {
      payload.start = start;
      payload.length = pageCount;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageCount]);

  const previousPayloadRef = useRef(null);
  const handleClearFilters = () => {
    isClear.current = true;
    setFormData((prevState) => ({
      ...prevState,
      search: {
        ...prevState.search,
        filters: prevState.search.filters.map((filter) => ({
          ...filter,
          value: "", // Reset each filter value to an empty string
        })),
      },
    }));
    setTempFilters([]);
    return true;
  };

  useEffect(() => {
    // Check if all filter values are empty
    const areFiltersEmpty = tempFilters?.every((filter) => filter.value === "");
    if (areFiltersEmpty) {
      if (isClear.current) {
        fetchMonitors();
        isClear.current = false;
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.search?.filters]); // Trigger when formData changes

  useEffect(() => {
    // Check if all filter values are empty
    const areFiltersEmpty = tempFilters?.every((filter) => filter.value === "");

    if (areFiltersEmpty) {
      const payload = { ...formData };

      // Ensure that the payload is different from the previous one
      if (!_.isEqual(previousPayloadRef.current, payload)) {
        previousPayloadRef.current = payload;

        fetchMonitors(); // Replace with your fetch function
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, formData?.search?.filters]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      search: {
        ...prevData.search,
        value: searchTerm,
      },
    }));
  }, [searchTerm]);

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    _.debounce((searchTerm) => {
      setActivePage(1);
      setSearchTerm(searchTerm);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(search);
    return () => {
      debouncedSearch.cancel();
    };
  }, [search, debouncedSearch]);

  const closeFilters = () => {
    if (isSubmit.current === false) {
      isClear.current = false;
      setFormData((prevState) => ({
        ...prevState,
        search: {
          ...prevState.search,
          filters: prevState.search.filters.map((filter) => ({
            ...filter,
            value: "",
          })),
        },
      }));
    }
    const offcanvasElement = document.getElementById("monitorfilter");
    const offcanvasInstance = bootstrap.Offcanvas.getInstance(offcanvasElement);

    if (offcanvasInstance) {
      offcanvasInstance.hide();
    } else {
      console.error("Offcanvas instance not found or already closed.");
    }
  };

  const openFilters = () => {
    setTempFilters(_.cloneDeep(formData.search.filters));
    const offcanvasElement = document.getElementById("monitorfilter");
    if (offcanvasElement) {
      const offcanvasInstance =
        bootstrap.Offcanvas.getOrCreateInstance(offcanvasElement);
      offcanvasInstance.show();
    }
  };

  useEffect(() => {
    const filtersWithValue = formData?.search?.filters.filter(
      (filter) => filter.value !== ""
    );
    setActiveFiltersLength(filtersWithValue.length);
  }, [formData?.search?.filters]);

  return (
    <main id="content" role="main" className="main">
      <div className="content">
        <div
          className="page-header row"
          style={{
            paddingRight: "20px",
            paddingLeft: "20px",
            paddingBottom: "0",
          }}
        >
          <div className="row align-items-center mb-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb breadcrumb-no-gutter">
                <li className="breadcrumb-item">
                  {/* eslint-disable-next-line */}
                  <a className="breadcrumb-link" href="javascript:;">
                    <span
                      style={{
                        color: "#919293",
                        fontSize: "18px",
                        fontWeight: "600",
                      }}
                    >
                      {t("Baclinks")}
                    </span>
                  </a>
                </li>
              </ol>
            </nav>

            <div className="col-md d-md-flex justify-content-between align-items-center mb-sm-2 mb-2 mb-md-0">
              <h1
                className="page-header-title"
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                }}
              >
                {t("Backlinks Monitor")}
              </h1>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="card" style={{ border: "2px solid var(--border)" }}>
            <div className="card-header contract_header_container justify-content-between d-flex flex-wrap gap-2">
              <div className="search-input-container">
                <div className="input-group input-group-merge input-group-flush border rounded-2 pe-2">
                  <div className="input-group-prepend input-group-text px-2">
                    <i className="bi-search"></i>
                  </div>
                  <input
                    id="datatableSearch"
                    type="search"
                    className="form-control custom-placeholder"
                    placeholder={t("Search")}
                    aria-label="Search"
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-grid d-flex align-items-center gap-2 monitor_header_btn">
                <div className="btn-group dropupend">
                  <button
                    className="btn btn-white text-nowrap"
                    type="button"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#monitorfilter"
                    aria-controls="monitorfilter"
                    onClick={openFilters}
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      height: "47px !important",
                      width: "137px !important",
                    }}
                  >
                    <FilterIcon height="24" width="24" />{" "}
                    <span className="ps-1 align-middle filter-btn">
                      {t("Filters")}{" "}
                      {activeFiltersLength > 0 && `(${activeFiltersLength})`}
                    </span>{" "}
                  </button>
                </div>

                <div
                  className="dropdown"
                  style={{
                    height: "47px !important",
                    width: "133px !important",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-white text-nowrap filter-btn column-button-contract"
                    id="showHideDropdown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    data-bs-auto-close="outside"
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    <i className="bi bi-layout-three-columns me-1 filter-btn"></i>{" "}
                    {t("Columns")}
                  </button>

                  <div
                    className="dropdown-menu dropdown-menu-end dropdown-card"
                    aria-labelledby="showHideDropdown"
                    style={{ width: "15rem" }}
                  >
                    <div className="card card-sm">
                      <div className="card-body">
                        <div className="d-grid gap-3">
                          {Object.keys(columns)
                            ?.slice(1)
                            .map((key) => (
                              <label
                                key={key}
                                className="row form-check form-switch"
                                htmlFor={`toggleColumn_${key}`}
                              >
                                <span className="col-8 col-sm-9 ms-0">
                                  <span className="me-2">
                                    {t(_.startCase(key))}
                                  </span>
                                </span>
                                <span className="col-4 col-sm-3 text-end">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`toggleColumn_${key}`}
                                    checked={columns[key]}
                                    onChange={() => handleToggle(key)}
                                  />
                                </span>
                              </label>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <MonitorTable
              data={tableData}
              setData={setTableData}
              columnsVisibility={columns}
              setColumnSorts={setColumnSorts}
              columnSorts={columnSorts}
            />

            <TablePagination
              activePage={activePage}
              setActivePage={setActivePage}
              recordsFiltered={recordsFiltered}
              setPageCount={setPageCount}
              pageCount={pageCount}
            />
          </div>
        </div>
      </div>

      <MonitorFilterModal
        data={formData}
        tempFilters={tempFilters}
        setTempFilters={setTempFilters}
        setData={setFormData}
        fetchData={fetchMonitors}
        handleClear={handleClearFilters}
        closeModal={closeFilters}
        isSubmit={isSubmit}
      />
      <Footer />
    </main>
  );
};

export default Monitor;
